import React from "react";
import { Spinner, Icon, Collapse, Tooltip } from "@blueprintjs/core";
import FirstProductPrompt from "../FirstProductPrompt/FirstProductPrompt";
import { useTable, useFlexLayout, useExpanded } from "react-table";
import classnames from "classnames";
import styles from "./ProductList.module.scss";
import customTableComponents from "./ProductListTableComponents";
import currency from "currency-formatter";
import ProductOpportunityLozenge from "./ProductOpportunityLozenge";
import { useHistory } from "react-router-dom";
import moment from "moment";

function ProductList({
  products,
  isLoading,
  onProductRowClick,
  columns,
  onAddProductClick,
  isProductListScrollLoading,
  searchText,
  onUploadCSVClick
}) {
  const CellRenderer = customTableComponents();
  const history = useHistory();
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable(
    {
      columns,
      data: products || [],
      manualSortBy: true,
      defaultColumn: { width: 100 }
    },
    useFlexLayout,
    useExpanded
  );

  if (isLoading || !products) return <Spinner />;

  if (!isLoading && products && !products.length && !searchText) {
    return (
      <div className={styles.promptWrapper}>
        <FirstProductPrompt
          onAddProductClick={onAddProductClick}
          onUploadCSVClick={onUploadCSVClick}
        />
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <div {...getTableProps()} className={styles.table}>
        <div className={styles.headerRow}>
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()} className={styles.tr}>
              {headerGroup.headers.map((column) => (
                <div {...column.getHeaderProps()} className={styles.th}>
                  {column.render("Header")}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <div key={row.id}>
                <div
                  {...row.getRowProps()}
                  className={classnames(
                    styles.tr,
                    row.isExpanded && styles.expanded
                  )}
                >
                  {row.cells.map((cell, index) => {
                    if (cell.column.id == "expander") {
                      return (
                        <div {...cell.getCellProps()} className={styles.td}>
                          <span
                            className={styles.btnEdit}
                            onClick={() => onProductRowClick(row.original)}
                          >
                            <Tooltip
                              popoverClassName={styles.infoTooltip}
                              content="Edit"
                              position="bottom"
                            >
                              <Icon icon="edit" />
                            </Tooltip>
                          </span>
                          <span
                            className={styles.btnEdit}
                            onClick={() =>
                              history.push(
                                `/products/${row.original.id}/confirmation-email`
                              )
                            }
                          >
                            <Tooltip
                              popoverClassName={styles.infoTooltip}
                              content="Confirmation Email"
                              position="bottom"
                            >
                              <Icon icon="envelope" />
                            </Tooltip>
                          </span>
                          {(row.original.activeOpportunities.length > 0 ||
                            row.original.variants.length > 0) && (
                            <span
                              className={styles.btnViewDetail}
                              {...row.getToggleRowExpandedProps()}
                              title=""
                            >
                              <Tooltip
                                popoverClassName={styles.infoTooltip}
                                content={row.isExpanded ? "Collapse" : "Expand"}
                                position="bottom"
                              >
                                <Icon
                                  icon={row.isExpanded ? "ban-circle" : "add"}
                                />
                              </Tooltip>
                            </span>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div {...cell.getCellProps()} className={styles.td}>
                        <CellRenderer cell={cell} className={styles.cellText} />
                      </div>
                    );
                  })}
                </div>
                <Collapse isOpen={row.isExpanded}>
                  <div className={styles.detailsContainer}>
                    <div className={styles.detailsRow}>
                      {row.original.variants.length > 0 && (
                        <div className={styles.detailsColumn}>
                          <h3>Product Vairant(s)</h3>
                          {row.original.variants.map((variant) => (
                            <div className={styles.detailsColumnRow}>
                              <div className={styles.colRight}>
                                <h5>{variant.sku}</h5>
                              </div>
                              <div className={styles.colLeft}>
                                <span>
                                  $
                                  {currency.format(variant.price, {
                                    format: "USD"
                                  })}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {row.original.activeOpportunities.length > 0 && (
                        <div className={styles.detailsColumn}>
                          <h3>Product Opportunities</h3>
                          {row.original.activeOpportunities.map(
                            (activeOpportunity) => (
                              <div className={styles.detailsColumnRow}>
                                <div className={styles.colRight}>
                                  <ProductOpportunityLozenge
                                    opportunities={row.original.opportunities}
                                    activeOpportunity={activeOpportunity}
                                  />
                                </div>
                                <div className={styles.colLeft}>
                                  {!activeOpportunity ||
                                  (!row.original.opportunities ||
                                    !row.original.opportunities.length) ? (
                                    <span>--</span>
                                  ) : (
                                    <span className={styles.redText}>
                                      Expires on{" "}
                                      {moment(
                                        activeOpportunity.expiresAt
                                      ).format("MMMM D, YYYY")}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Collapse>
              </div>
            );
          })}
        </div>
      </div>
      {!products.length && (
        <span className={styles.productNotFound}>Products Not Found</span>
      )}
      {isProductListScrollLoading && <Spinner />}
    </div>
  );
}

export default ProductList;
